/**
 * Liquid Glass — cross-browser refraction for the closed-drawer peek strip.
 *
 * Architecture:
 *   .wb-glass-refraction div (injected into .wheelbuilder-panel):
 *     1. backdrop-filter: blur(3px) → captures real page content behind the panel
 *     2. filter: url(#wb-glass-peek) → feDisplacementMap distorts those captured pixels
 *
 * Map Generation:
 *   Generates a 128x128 displacement map on the fly, scaled to the exact pixel
 *   dimensions of the panel. This ensures the curved bezel is always exactly ~56px
 *   tall, regardless of the panel's total height.
 */
(function wbGlassInit() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__wbGlassInited) return;
  window.__wbGlassInited = true;

  const FILTER_ID = 'wb-glass-peek';
  const DIV_CLASS = 'wb-glass-refraction';
  const rootEl = document.documentElement;

  const hasBackdropUrlSyntax =
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    (CSS.supports('backdrop-filter', 'url(#x)') ||
      CSS.supports('-webkit-backdrop-filter', 'url(#x)'));
  const ua = navigator.userAgent || '';
  const isChromiumEngine =
    /(Chrome|Chromium|Edg|OPR)\/\d+/i.test(ua) &&
    !/(CriOS|FxiOS|EdgiOS)/i.test(ua);
  const canUseBackdropSvg = hasBackdropUrlSyntax && isChromiumEngine;
  rootEl.classList.toggle('wb-has-backdrop-svg', !!canUseBackdropSvg);

  const N1 = 1.0;
  const N2 = 1.52;
  const SCALE_PX = 56;
  const NEUTRAL = 128;
  const CANVAS_SIZE = 128;

  function surfaceH(x) {
    return Math.pow(1 - Math.pow(Math.max(0, 1 - x), 4), 0.25);
  }

  function refractMag(x) {
    if (x <= 0 || x >= 1) return 0;
    const d = 5e-4;
    const deriv = (surfaceH(x + d) - surfaceH(x - d)) / (2 * d);
    const len = Math.sqrt(deriv * deriv + 1);
    const normX = -deriv / len;
    const sinI = Math.abs(normX);
    const sinR = (N1 / N2) * sinI;
    if (sinR >= 1) return 0;
    const tanI = sinI / Math.sqrt(Math.max(1e-9, 1 - sinI * sinI));
    const tanR = sinR / Math.sqrt(Math.max(1e-9, 1 - sinR * sinR));
    return Math.abs(tanR - tanI);
  }

  const samples = new Float32Array(CANVAS_SIZE);
  for (let i = 0; i < CANVAS_SIZE; i++) samples[i] = refractMag(i / CANVAS_SIZE);
  const maxD = Math.max(...Array.from(samples), 1e-6);

  let currentW = 0;
  let currentH = 0;
  let svgEl = null;

  function updateGlass() {
    const panel = document.querySelector('.wheelbuilder-panel');
    if (!panel) return;
    const w = panel.offsetWidth || window.innerWidth;
    const h = panel.offsetHeight || window.innerHeight;
    if (w === 0 || h === 0) return;
    
    // Only regenerate if dimensions changed significantly
    if (Math.abs(currentW - w) < 10 && Math.abs(currentH - h) < 10) return;
    currentW = w;
    currentH = h;

    // Keep the bezel exactly 56px top, 48px sides regardless of panel size
    const bezelPctY = Math.min(1, 56 / h);
    const bezelPctX = Math.min(1, 48 / w);

    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = CANVAS_SIZE;
    const ctx = canvas.getContext('2d');
    const img = ctx.createImageData(CANVAS_SIZE, CANVAS_SIZE);
    const px = img.data;

    for (let cy = 0; cy < CANVAS_SIZE; cy++) {
      for (let cx = 0; cx < CANVAS_SIZE; cx++) {
        const fy = cy / (CANVAS_SIZE - 1);
        const fx = cx / (CANVAS_SIZE - 1);

        const dTop = bezelPctY > 0 ? Math.min(1, fy / bezelPctY) : 1;
        const dLeft = bezelPctX > 0 ? Math.min(1, fx / bezelPctX) : 1;
        const dRight = bezelPctX > 0 ? Math.min(1, (1 - fx) / bezelPctX) : 1;

        const s = (v) => samples[Math.min(CANVAS_SIZE - 1, Math.floor(v * CANVAS_SIZE))];
        
        const magTop = s(dTop) / maxD;
        const yDisp = magTop * (1 - dTop) * 2.5;

        const magLeft = s(dLeft) / maxD;
        const xL = magLeft * (1 - dLeft) * 1.5;

        const magRight = s(dRight) / maxD;
        const xR = -magRight * (1 - dRight) * 1.5;

        const xDisp = xL + xR;

        const i4 = (cy * CANVAS_SIZE + cx) * 4;
        px[i4]     = Math.max(0, Math.min(255, Math.round(NEUTRAL + xDisp * 127)));
        px[i4 + 1] = Math.max(0, Math.min(255, Math.round(NEUTRAL + yDisp * 127)));
        px[i4 + 2] = NEUTRAL;
        px[i4 + 3] = 255;
      }
    }
    ctx.putImageData(img, 0, 0);
    const mapDataUrl = canvas.toDataURL('image/png');

    if (!svgEl) {
      svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svgEl.setAttribute('aria-hidden', 'true');
      svgEl.setAttribute('focusable', 'false');
      svgEl.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;';
      document.body.insertBefore(svgEl, document.body.firstChild);
    }

    svgEl.innerHTML = `<defs>
      <filter id="${FILTER_ID}"
              x="0" y="0" width="${w}" height="${h}"
              filterUnits="userSpaceOnUse"
              primitiveUnits="userSpaceOnUse"
              color-interpolation-filters="sRGB">
        <feImage
          href="${mapDataUrl}"
          xlink:href="${mapDataUrl}"
          x="0" y="0"
          width="${w}" height="${h}"
          preserveAspectRatio="none"
          result="disp_map"
        />
        <feDisplacementMap
          in="SourceGraphic"
          in2="disp_map"
          scale="${SCALE_PX}"
          xChannelSelector="R"
          yChannelSelector="G"
        />
      </filter>
    </defs>`;
  }

  function injectGlassLayers() {
    document.querySelectorAll('.wheelbuilder-panel').forEach((panel) => {
      let div = panel.querySelector('.' + DIV_CLASS);
      if (!div) {
        div = document.createElement('div');
        div.className = DIV_CLASS;
        div.setAttribute('aria-hidden', 'true');
        panel.prepend(div);
      }
      const oldBadge = panel.querySelector('.wb-glass-debug-badge');
      if (oldBadge) oldBadge.remove();
    });
  }

  let resizeTimer = null;
  function onResize() {
    clearTimeout(resizeTimer);
    if (canUseBackdropSvg) {
      resizeTimer = setTimeout(updateGlass, 150);
    }
  }

  function boot() {
    injectGlassLayers();
    if (canUseBackdropSvg) {
      updateGlass();
    }
    window.addEventListener('resize', onResize, { passive: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('alpine:initialized', () => {
    injectGlassLayers();
    if (canUseBackdropSvg) {
      updateGlass();
    }
  });
})();
